import React, { useState, useEffect, useCallback, useMemo } from 'react'
import cloneDeep from 'lodash/cloneDeep'
import get from 'lodash/get'
import set from 'lodash/set'
import sortBy from 'lodash/sortBy'
import moment from 'moment'
import DragDropFunctions from '~/utils/TemplateEditor/dragDropFunctions'
import Button from '~/Common-Objects/UI/Forms/Button/Button'
import LoadingIcons from '~/Common-Objects/LoadingIcons/loadingIcon1'
import TabsSimple from '~/Common-Objects/Tabs/TabsSimple'
import Expander from '~/Common-Objects/Expander'
import ComponentText from './Components/text'
import ComponentWysiwyg from './Components/wysiwyg'
import ComponentSelect from './Components/select'
import ColorPicker from './Components/colorPicker'
import HorizontalAlign from './Components/horizontalAlign'
import VerticalAlign from './Components/verticalAlign'
import InputTool from './Components/inputTool'
import ImagePicker from './Components/imagePicker'
import Breaker from './Components/breaker'
import Video from './Components/video'
import VimeoVideo from './Components/vimeoVideo'
import DailyMotionVideo from './Components/dailyMotionVideo'
import CustomField from './Components/customField'
import HTMLBlock from './Components/htmlBlock'
import ArraySelect from './Components/arraySelect'
import MenuLinks from './Components/menuLinks'
import ClusterHolder from './Components/clusterHolder'
import MarginPaddingHolder from './Components/MarginPaddingHolder'
import BorderRadiusHolder from './Components/BorderRadiusHolder'
import CodeEditor from './Components/codeEditor'
import PollPicker from './Components/pollPicker'
import GoogleMaps from './Components/googleMaps'
import RssEditor from './Components/rssEditor'
import TagManager from './Components/tagManager'
import Explainer from './Components/explainer'
import Toggle from './Components/toggle'
import { isDynamicImageValue } from '~/utils/TemplateEditor/landingBackground'
import { resolveParentCategoryName, translateCategoryName } from '~/utils/TemplateEditor/editorCategoryHierarchy'
import { takesExplicitOnChangeValue } from './explicitOnChangeValue'
import i18n from '~/i18n'
import PanelHeader from '../../PanelHeader'
import ButtonPreview from './Components/buttonPreview'
import InlineFormFields from './Components/InlineFormFields'
import InlineFormSettings from './Components/inlineFormSettings'
import CreateEmbeddedFormLink from './Components/createEmbeddedFormLink'

// Expander headings are stored in English; show them in the user's language.
const categoryLabel = (name) => translateCategoryName(name, (key, options) => i18n.t(key, options))

export default function ElementEditor(props) {
    const [currentTab, setCurrentTab] = useState(null)
    const [thisElement, setThisElement] = useState([])
    const [loaded, setLoaded] = useState(false)
    const [externalDataError, setExternalDataError] = useState(false)

    const init = useCallback(
        async (theProps, preserveTab = false) => {
            var selectedElement = theProps.selectedElement

            var currentTab
            var thisElement

            if (
                selectedElement.path &&
                selectedElement.path != '' &&
                selectedElement.path != null &&
                selectedElement.path != undefined
            ) {
                thisElement = cloneDeep(get(theProps.elementsData, selectedElement.path))
            } else {
                thisElement = cloneDeep(theProps.elementsData.globalVariables)
            }
            if (thisElement && (thisElement.editableTabs || thisElement.editableTabsNew)) {
                const tabs = thisElement.editableTabsNew || thisElement.editableTabs
                currentTab = tabs[0]
                if (!preserveTab) {
                    const defaultTab = thisElement.defaultEditableTab || thisElement.defaultCatagoryGroup
                    if (defaultTab && tabs.includes(defaultTab)) {
                        currentTab = defaultTab
                    }
                }
            }

            let dataError = false
            if (thisElement && thisElement.localVariables) {
                let newLocalVars = await DragDropFunctions.checkForExternalData(thisElement.localVariables, {
                    landingPageId: theProps.landingPageId
                })
                if (newLocalVars?.errors?.length) {
                    dataError = true
                }
                if (newLocalVars?.localVars) {
                    thisElement.localVariables = newLocalVars.localVars
                }
                if (newLocalVars?.updated && newLocalVars?.localVars && selectedElement.path) {
                    const elementsData = cloneDeep(theProps.elementsData)
                    set(elementsData, selectedElement.path + '.localVariables', newLocalVars.localVars)
                    theProps.updateElementsData(elementsData, selectedElement.path)
                }
            }
            if (preserveTab) {
                setCurrentTab((prev) => {
                    const tabs = thisElement?.editableTabsNew || thisElement?.editableTabs
                    if (prev && tabs?.includes(prev)) return prev
                    return currentTab
                })
            } else {
                setCurrentTab(currentTab)
            }
            setThisElement(thisElement)
            setLoaded(true)
            setExternalDataError(dataError)
        },
        [props.selectedElement.path, props.selectedElement.isSelected]
    )

    const requiredToShowProcessor = function (theOpt, localVariables, thisElement) {
        if (!theOpt.requiredToShow) return true

        function requiredToShowProcessorHelper(rule) {
            if (rule.propertyName && rule.propertyValue) {
                let lookupName = rule.propertyName
                const ruleBreakpoint =
                    theOpt.followCanvasBreakpoint && props.isLandingPage
                        ? props.canvasScreenType === 1
                            ? 'mobile'
                            : props.canvasScreenType === 2
                              ? 'tablet'
                              : 'desktop'
                        : 'desktop'
                if (rule.matchBreakpoint && ruleBreakpoint && ruleBreakpoint !== 'desktop') {
                    const suffix = ruleBreakpoint === 'tablet' ? 'Tablet' : 'Mobile'
                    lookupName = rule.propertyName.replace('_localVariables_.', '') + suffix
                }
                const localKey = lookupName.replace('_localVariables_.', '')
                const localEntry = localVariables && localVariables[localKey]
                let requiredLocalVariableVal =
                    localEntry && typeof localEntry === 'object' && localEntry.value != null
                        ? localEntry.value
                        : DragDropFunctions.lookupLocalVariables(localVariables, lookupName)

                if (requiredLocalVariableVal == lookupName) {
                    //try other props in this ele (for global theme style etc)
                    if (thisElement[rule.propertyName]) {
                        requiredLocalVariableVal = thisElement[rule.propertyName].value
                    }
                }

                if (rule.matchBreakpoint && ruleBreakpoint !== 'desktop') {
                    if (requiredLocalVariableVal == lookupName || requiredLocalVariableVal == '') {
                        return false
                    }
                }

                if (rule.propertyValue == 'not none') {
                    if (requiredLocalVariableVal == 'none' || requiredLocalVariableVal == '') {
                        return false
                    }
                } else if (rule.propertyValue == 'not bare') {
                    if (requiredLocalVariableVal == 'bare' || requiredLocalVariableVal == '') {
                        return false
                    }
                } else if (rule.propertyValue == 'isDynamic') {
                    if (!isDynamicImageValue(requiredLocalVariableVal)) {
                        return false
                    }
                } else {
                    if (requiredLocalVariableVal !== rule.propertyValue) {
                        return false
                    }
                }
            }
            if (rule.customFieldTypes && rule.propertyName) {
                let currentCustomFieldId = DragDropFunctions.lookupLocalVariables(localVariables, rule.propertyName)
                let currentCustomField = DragDropFunctions.customFields[currentCustomFieldId]
                if (currentCustomField) {
                    if (!rule.customFieldTypes.includes(currentCustomField.type)) {
                        return false
                    }
                }
                if (!currentCustomField) {
                    return false
                }
            }
            return true
        }

        if (Array.isArray(theOpt.requiredToShow)) {
            let ok = true
            theOpt.requiredToShow.forEach((rule) => {
                if (!requiredToShowProcessorHelper(rule)) {
                    ok = false
                }
            })
            return ok
        }
        return requiredToShowProcessorHelper(theOpt.requiredToShow)
    }

    // Expander order is first-seen newCategory after the editableOrder sort.
    const buildCategoryHierarchy = useCallback((editableOptions, currentTab) => {
        const tabFilteredOptions = editableOptions.filter((option) => {
            if (option.hiddenFromRightTools) return false
            return option.editableTab === currentTab
        })

        const categoriesMap = new Map()
        tabFilteredOptions.forEach((option) => {
            const parentName = resolveParentCategoryName(option)
            const childName = option.category || null

            // Ensure parent category exists
            let parentCat = categoriesMap.get(parentName)
            if (!parentCat) {
                parentCat = {
                    name: parentName,
                    isParent: true,
                    children: []
                }
                categoriesMap.set(parentName, parentCat)
            }

            // Ensure child category exists under parent
            let childCat = parentCat.children.find((c) => c.name === childName)
            if (!childCat) {
                childCat = {
                    name: childName,
                    items: []
                }
                parentCat.children.push(childCat)
            }
            if (option.categoryGroupExpandedNew) {
                parentCat.expanded = true
            }

            // Push item into child
            childCat.items.push(option)
        })

        //check for sub menu items
        categoriesMap.forEach((category) => {
            let subMenuItems = []
            category.children.forEach((child, childIndex) => {
                const itemsToRemove = []
                const remainingItems = []

                child.items.forEach((item, childItemIndex) => {
                    let eligableForSubMenu = item.newCategory && item.categoryGroup && item.newCategorySubCategory
                    if (eligableForSubMenu) {
                        itemsToRemove.push(childItemIndex)
                        let foundMenuItem = subMenuItems.find(
                            (c) => c.newCategory == item.newCategory && c.categoryGroup == item.categoryGroup
                        )
                        if (!foundMenuItem) {
                            subMenuItems.push({
                                type: 'categorySubMenu',
                                newCategory: item.newCategory,
                                categoryGroup: item.categoryGroup,
                                menuItems: [item.newCategorySubCategory],
                                children: [item]
                            })
                        } else {
                            let foundSubMenuItem = foundMenuItem.menuItems.find((c) => c == item.newCategorySubCategory)
                            if (!foundSubMenuItem) {
                                foundMenuItem.menuItems.push(item.newCategorySubCategory)
                                foundMenuItem.children.push(item)
                            } else {
                                foundMenuItem.children.push(item)
                            }
                        }
                    } else {
                        remainingItems.push(item)
                    }
                })
                child.items = remainingItems
            })
            if (subMenuItems.length > 0) {
                subMenuItems.forEach((subMenuItem) => {
                    subMenuItem.children.sort((a, b) => {
                        const aOrder = typeof a.editableOrder === 'number' ? a.editableOrder : Number(a.editableOrder)
                        const bOrder = typeof b.editableOrder === 'number' ? b.editableOrder : Number(b.editableOrder)

                        if (!Number.isNaN(aOrder) && !Number.isNaN(bOrder) && aOrder !== bOrder) {
                            return aOrder - bOrder
                        }

                        const nameA = a.label || ''
                        const nameB = b.label || ''
                        return nameA.localeCompare(nameB)
                    })
                })
                category.selectionOptions = subMenuItems
            }
        })

        return Array.from(categoriesMap.values())
    }, [])

    useEffect(() => {
        setLoaded(false)
        init(props)
    }, [props.selectedElement.path, props.selectedElement.isSelected, init])

    useEffect(() => {
        if (!loaded || !props.globalStyleFocus?.category) return
        const category = props.globalStyleFocus.category

        const scrollToCategory = () => {
            const panel = document.querySelector('.dndEditorv2-leftTools-panel--active')
            const el = (panel || document).querySelector(`[data-category="${category}"]`)
            if (!el) return
            const scroller = el.closest('.dndEditorv2-leftTools-panel')
            if (scroller) {
                const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop
                scroller.scrollTo({ top: Math.max(0, top), behavior: 'smooth' })
                return
            }
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }

        // Wait for sibling expanders to finish collapsing (200ms) so the target offset is correct.
        const timer = setTimeout(scrollToCategory, 220)
        return () => clearTimeout(timer)
    }, [loaded, props.globalStyleFocus])

    const updateVal = useCallback(
        async (thePaths, theVal, property) => {
            if (!Array.isArray(thePaths)) {
                thePaths = [thePaths]
            }
            // When multiple paths are passed with a matching-length values array, pair them;
            // otherwise broadcast a single value (including when that value is itself an array).
            const useParallelVals = thePaths.length > 1 && Array.isArray(theVal) && theVal.length === thePaths.length
            const theVals = useParallelVals ? theVal : thePaths.map(() => theVal)
            var theElementsData = cloneDeep(props.elementsData)
            var selectedElement = props.selectedElement
            var thePathFinal = ''
            let shouldRefreshElement = false

            for (let pathIndex = 0; pathIndex < thePaths.length; pathIndex++) {
                const thePath = thePaths[pathIndex]
                const currentVal = theVals[pathIndex]
                var thisProp = get(thisElement, thePath)

                //if we are updating this element, or a global variable
                if (
                    selectedElement.path &&
                    selectedElement.path != '' &&
                    selectedElement.path != null &&
                    selectedElement.path != undefined
                ) {
                    thePathFinal = selectedElement.path + thePath
                    set(theElementsData, thePathFinal, thisProp)
                    set(theElementsData, thePathFinal + '.contentDate', moment())
                } else {
                    //html/body/global variables
                    thePathFinal = 'globalVariables.' + thePath
                    theElementsData.elements[0].contentDate = moment()
                }

                var finalVal = ''
                if (typeof currentVal == 'object' && currentVal !== null && currentVal.hasOwnProperty('value')) {
                    finalVal = currentVal.value
                } else if (typeof currentVal == 'boolean') {
                    finalVal = String(currentVal)
                } else {
                    finalVal = currentVal
                }

                //if it is updating another property other than value
                if (property) {
                    thisProp[property] = finalVal
                } else {
                    thisProp.value = finalVal
                }

                var promises = []
                //if the element has an onchange parameter, go through it.
                if (thisProp.onChange) {
                    if (thisProp.onChange.ignoreValue && thisProp.onChange.ignoreValue.indexOf(currentVal) > -1) {
                        set(theElementsData, thePathFinal, thisProp)
                    } else {
                        if (thisProp.onChange.update) {
                            var toChange = thisProp.onChange.update
                            if (Array.isArray(toChange)) {
                                for (var each in toChange) {
                                    var thisPath
                                    if (
                                        selectedElement.path &&
                                        selectedElement.path !== '' &&
                                        selectedElement.path !== null &&
                                        selectedElement.path !== undefined
                                    ) {
                                        thisPath = selectedElement.path + '.' + toChange[each].path
                                    } else {
                                        thisPath = 'globalVariables.' + toChange[each].path
                                    }

                                    if (toChange[each].condition) {
                                        if (toChange[each].condition == 'ifBlank') {
                                            if (toChange[each].path) {
                                                if (toChange[each].path.includes('localVariables')) {
                                                    let ourElement = get(theElementsData, selectedElement.path)
                                                    let localVariables = ourElement.localVariables
                                                    let rawPath = toChange[each].path.split('.')
                                                    let rawPathVal = DragDropFunctions.lookupLocalVariables(
                                                        localVariables,
                                                        rawPath[1]
                                                    )
                                                    if (
                                                        rawPathVal != '' &&
                                                        rawPathVal != null &&
                                                        rawPathVal != undefined
                                                    ) {
                                                        //if value is already set stop here
                                                        continue
                                                    }
                                                }
                                            }
                                        }
                                    }

                                    if (takesExplicitOnChangeValue(toChange[each])) {
                                        set(theElementsData, thisPath, toChange[each].value)
                                    } else {
                                        var valueType = toChange[each].valueType

                                        if (toChange[each]?.updateType == 'stringReplace') {
                                            let moddedVal = toChange[each].newValue.replace(
                                                '[selectedValue]',
                                                currentVal
                                            )
                                            set(theElementsData, thisPath, moddedVal)
                                        } else {
                                            if (typeof currentVal == 'object') {
                                                var ourVal = String(currentVal[valueType])
                                                if (valueType == 'required' && ourVal == 'false') ourVal = ''
                                                if (valueType == 'required' && ourVal == 'true') ourVal = 'required'

                                                if (toChange[each]?.valueType == 'customFieldArray') {
                                                    const cFieldOptions = DragDropFunctions.resolveCustomFieldOptions(
                                                        typeof currentVal === 'object' ? currentVal.value : currentVal
                                                    )
                                                    if (cFieldOptions !== null) {
                                                        ourVal = cFieldOptions
                                                    } else {
                                                        ourVal = []
                                                    }
                                                }

                                                if (
                                                    toChange[each]?.valueType == 'customFieldArray' ||
                                                    toChange[each]?.valueType == 'value'
                                                ) {
                                                    shouldRefreshElement = true
                                                }

                                                set(theElementsData, thisPath, ourVal)
                                            } else {
                                                set(theElementsData, thisPath, ourVal)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                        if (thisProp.onChange.getData && thisElement && thisElement.localVariables) {
                            //reloads all external data in the app
                            promises.push(
                                DragDropFunctions.checkForExternalData(thisElement.localVariables, {
                                    landingPageId: props.landingPageId
                                })
                            )
                        }
                        if (thisProp.onChange.syncData && thisElement?.localVariables) {
                            const canvasFormId = get(
                                theElementsData,
                                selectedElement.path + '.localVariables.formId.value'
                            )
                            if (
                                canvasFormId &&
                                thisElement.localVariables.formId &&
                                !thisElement.localVariables.formId.value
                            ) {
                                thisElement.localVariables.formId.value = canvasFormId
                            }
                            const editorContext = { landingPageId: props.landingPageId }
                            promises.push(
                                DragDropFunctions.checkForExternalData(thisElement.localVariables, editorContext).then(
                                    (postResult) => {
                                        if (postResult?.updated && postResult.localVars) {
                                            set(
                                                theElementsData,
                                                selectedElement.path + '.localVariables',
                                                postResult.localVars
                                            )
                                            thisElement.localVariables = postResult.localVars
                                        }
                                        return DragDropFunctions.checkForExternalData(
                                            thisElement.localVariables,
                                            editorContext,
                                            { syncProp: thisProp, syncValue: finalVal }
                                        )
                                    }
                                )
                            )
                        }
                    }
                }
                set(theElementsData, thePathFinal, thisProp)
            }

            if (selectedElement.path && thePaths.some((p) => p.indexOf('localVariables.') === 0)) {
                set(theElementsData, selectedElement.path + '.contentDate', Date.now())
            }

            return Promise.all(promises).then((_newLocalVariables) => {
                if (_newLocalVariables && _newLocalVariables.length) {
                    _newLocalVariables.forEach((result) => {
                        if (result?.updated && result.localVars) {
                            set(theElementsData, selectedElement.path + '.localVariables', result.localVars)
                        }
                    })
                }
                props.updateElementsData(theElementsData, selectedElement.path)
                const isInputFieldsUpdate = thePaths.some((p) => p.includes('inputFields'))
                if ((shouldRefreshElement || isInputFieldsUpdate) && selectedElement.path) {
                    setThisElement(cloneDeep(get(theElementsData, selectedElement.path)))
                }
                const isToggleUpdate = thePaths.some((p) => p.includes('Enabled'))
                if (isToggleUpdate) {
                    init({ ...props, elementsData: theElementsData }, true)
                }
                return true
            })
        },
        [thisElement, props]
    )

    // Components that write straight to elementsData instead of going through updateVal have to
    // re-sync this snapshot, otherwise the panel keeps rendering (and writing back) pre-change data.
    const refreshElement = useCallback(
        (theElementsData) => {
            if (!props.selectedElement.path) return
            setThisElement(cloneDeep(get(theElementsData || props.elementsData, props.selectedElement.path)))
        },
        [props.elementsData, props.selectedElement.path]
    )

    const changeTab = useCallback((tab) => {
        setCurrentTab(tab)
    }, [])

    const renderCatGroup = useCallback(
        (editableOptions, globalColours, currentCatGroup, showBreakers = true) => {
            if (!editableOptions) return null
            // Use thisElement here. The render-time `localVariables` below is
            // assigned after an early return, so a cached callback can still
            // see an empty object and hide pre-selected Custom fields.
            const localVariables = (thisElement && thisElement.localVariables) || {}

            function getComponentType(theOpt, index, globalColours, currentCatGroup) {
                var compType = theOpt.editableType
                if (theOpt.editableTypeV2) {
                    compType = theOpt.editableTypeV2
                }
                var theVal = theOpt.value
                var thePath = theOpt.path
                var theLabel = theOpt.label
                var htmlText = theOpt.HTMLText
                var theUnit = ''
                var options = {}
                var editableDirection = 'top'
                var editableUnits
                var localVariables = {}
                var globalVariables = {}
                var lineOut = ''

                globalVariables = props.elementsData.globalVariables

                if (thisElement.localVariables) {
                    localVariables = thisElement.localVariables
                }
                if (theOpt.editableUnit) {
                    theUnit = theOpt.editableUnit
                }

                if (theOpt.editableDirection) {
                    editableDirection = theOpt.editableDirection
                }
                if (theOpt.editableUnits) {
                    editableUnits = theOpt.editableUnits
                }

                var requiredErr = false
                if (theOpt.valueBlankWillError && !theVal) {
                    requiredErr = true
                }

                if (typeof theVal != 'string' && typeof theVal != 'object') {
                    theVal = String(theVal)
                }
                if (typeof theVal != 'object' && theVal.indexOf('global_') > 0) {
                    if (props.elementsData.globalVariables[theVal.split('_.')[1]]) {
                        theVal = props.elementsData.globalVariables[theVal.split('_.')[1]].value
                    }
                }

                if (typeof theVal != 'object' && theVal.indexOf('_localVariables_') >= 0) {
                    theVal = DragDropFunctions.lookupLocalVariables(localVariables, theVal)
                }

                if (theOpt.options) {
                    options = theOpt.options
                    if (options.indexOf('_localVariables_') >= 0) {
                        options = DragDropFunctions.lookupLocalVariables(localVariables, options)
                    }
                }

                const usesCanvasBreakpoint = !!(theOpt.followCanvasBreakpoint && props.isLandingPage)
                const activeBreakpoint = usesCanvasBreakpoint
                    ? props.canvasScreenType === 1
                        ? 'mobile'
                        : props.canvasScreenType === 2
                          ? 'tablet'
                          : 'desktop'
                    : 'desktop'
                if (usesCanvasBreakpoint && activeBreakpoint !== 'desktop') {
                    const suffix = activeBreakpoint === 'tablet' ? 'Tablet' : 'Mobile'
                    thePath = thePath + suffix
                    const key = thePath.split('.').pop()
                    const siblingVal =
                        localVariables[key] && localVariables[key].value != null ? localVariables[key].value : ''
                    theVal = siblingVal !== '' ? siblingVal : theVal
                }

                if (!requiredToShowProcessor(theOpt, localVariables, thisElement)) return null

                switch (compType) {
                    case 'Text':
                        lineOut = (
                            <ComponentText
                                campaign={props.campaign}
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                path={thePath}
                                elementsData={props.elementsData}
                                elementData={theOpt}
                                selectedElement={props.selectedElement}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'Wysiwyg':
                        lineOut = (
                            <ComponentWysiwyg
                                selectedElement={props.selectedElement}
                                formLandingPage={props.formLandingPage}
                                isFormLandingPage={props.isFormLandingPage}
                                campaign={props.campaign}
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                path={thePath}
                                globalColours={globalColours}
                                favoriteColors={props.elementsData.globalVariables.favoriteColors}
                                saveFaveColor={props.saveFaveColor}
                                elementData={theOpt}
                                elementsData={props.elementsData}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'Select':
                        lineOut = (
                            <ComponentSelect
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                options={options}
                                updateVal={updateVal}
                                path={thePath}
                                elementData={theOpt}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                                openGlobalStyle={props.openGlobalStyle}
                            />
                        )
                        break
                    case 'ColorPicker':
                        lineOut = (
                            <ColorPicker
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                path={thePath}
                                globalColours={globalColours}
                                favoriteColors={props.elementsData.globalVariables.favoriteColors}
                                saveFaveColor={props.saveFaveColor}
                                elementData={theOpt}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                                allowAlpha={!!theOpt.allowAlpha}
                            />
                        )
                        break
                    case 'UnitInput':
                        lineOut = (
                            <InputTool
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                path={thePath}
                                unit={theUnit}
                                className={editableDirection}
                                editableUnits={editableUnits}
                                elementData={theOpt}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'HorizontalAlign':
                        lineOut = (
                            <HorizontalAlign
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                options={options}
                                path={thePath}
                                elementData={theOpt}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'VerticalAlign':
                        lineOut = (
                            <VerticalAlign
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                options={options}
                                path={thePath}
                                elementData={theOpt}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                            />
                        )
                        break
                    case 'ImagePicker':
                        lineOut = (
                            <ImagePicker
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                options={options}
                                path={thePath}
                                elementData={theOpt}
                                localVariables={localVariables}
                                globalVariables={props.elementsData.globalVariables}
                                isDragging={props.isDragging}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'Video':
                        lineOut = (
                            <Video
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                options={options}
                                path={thePath}
                                elementData={theOpt}
                                templateId={props.templateId}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'VimeoVideo':
                        lineOut = (
                            <VimeoVideo
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                options={options}
                                path={thePath}
                                elementData={theOpt}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'DailyMotionVideo':
                        lineOut = (
                            <DailyMotionVideo
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                options={options}
                                path={thePath}
                                elementData={theOpt}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'GoogleMaps':
                        lineOut = (
                            <GoogleMaps
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                options={options}
                                path={thePath}
                                elementData={theOpt}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'CustomField':
                        lineOut = (
                            <CustomField
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                options={DragDropFunctions.customFields}
                                path={thePath}
                                elementsData={props.elementsData}
                                elementData={theOpt}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'HTMLBlock': //Use this to insert dangerously set HTML anywhere you like :D
                        // Existing landing page Form blocks still store the static create-form HTMLBlock.
                        // Intercept that so create-from-landing-page can carry returnTo.
                        if (
                            props.landingPageId &&
                            htmlText &&
                            htmlText.indexOf('Create a new form') > -1 &&
                            htmlText.indexOf('/cp/forms') > -1
                        ) {
                            lineOut = (
                                <CreateEmbeddedFormLink
                                    key={'Comp_' + index}
                                    landingPageId={props.landingPageId}
                                    saveTemplate={props.saveTemplate}
                                />
                            )
                        } else {
                            lineOut = (
                                <HTMLBlock
                                    key={'Comp_' + index}
                                    value={htmlText}
                                    elementData={theOpt}
                                    localVariables={localVariables}
                                    isLandingPage={props.isLandingPage}
                                    isForm={props.isForm}
                                />
                            )
                        }
                        break
                    case 'ArraySelect': //Use this to allow people to build array values
                        lineOut = (
                            <ArraySelect
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                path={thePath}
                                elementData={theOpt}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                                fieldLabelTxt={theOpt.fieldLabelTxt}
                                fieldValueTxt={theOpt.fieldValueTxt}
                            />
                        )
                        break
                    case 'MenuLinks':
                        lineOut = (
                            <MenuLinks
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                path={thePath}
                                elementData={theOpt}
                                elementsData={props.elementsData}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                            />
                        )
                        break
                    case 'html': //use this for code editor
                        lineOut = (
                            <CodeEditor
                                campaign={props.campaign}
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                options={DragDropFunctions.customFields}
                                path={thePath}
                                elementsData={props.elementsData}
                                elementData={theOpt}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'PollPicker':
                        lineOut = (
                            <PollPicker
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                options={DragDropFunctions.customFields}
                                path={thePath}
                                elementsData={props.elementsData}
                                elementData={theOpt}
                                localVariables={localVariables}
                                deselectElement={props.deselectElement}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'RssEditor':
                        lineOut = (
                            <RssEditor
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                options={DragDropFunctions.customFields}
                                path={thePath}
                                elementsData={props.elementsData}
                                elementData={theOpt}
                                localVariables={localVariables}
                                deselectElement={props.deselectElement}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'TagManager':
                        lineOut = (
                            <TagManager
                                error={requiredErr}
                                key={'Comp_' + index}
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                options={DragDropFunctions.customFields}
                                path={thePath}
                                elementsData={props.elementsData}
                                elementData={theOpt}
                                localVariables={localVariables}
                                deselectElement={props.deselectElement}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'Explainer':
                        lineOut = (
                            <Explainer
                                value={theVal}
                                elementData={theOpt}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break

                    case 'Toggle':
                        lineOut = (
                            <Toggle
                                value={theVal}
                                title={theLabel}
                                updateVal={updateVal}
                                path={thePath}
                                elementData={theOpt}
                                localVariables={localVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break

                    case 'ButtonPreview':
                        lineOut = (
                            <ButtonPreview
                                key={'Comp_' + index}
                                variant={theVal}
                                elementData={theOpt}
                                localVariables={localVariables}
                                globalVariables={globalVariables}
                                isLandingPage={props.isLandingPage}
                                isForm={props.isForm}
                            />
                        )
                        break
                    case 'InlineFormFields':
                        lineOut = (
                            <InlineFormFields
                                key={'Comp_' + index}
                                value={theVal}
                                elementData={theOpt}
                                updateVal={updateVal}
                                path={thePath}
                                landingPageId={props.landingPageId}
                                localVariables={localVariables}
                                elementsData={props.elementsData}
                                elementPath={props.selectedElement.path}
                            />
                        )
                        break
                    case 'InlineFormSettings':
                        lineOut = (
                            <InlineFormSettings
                                key={'Comp_' + index}
                                value={theVal}
                                elementData={theOpt}
                                updateVal={updateVal}
                                path={thePath}
                                landingPageId={props.landingPageId}
                                localVariables={localVariables}
                                elementsData={props.elementsData}
                                elementPath={props.selectedElement.path}
                                updateElementsData={props.updateElementsData}
                                refreshElement={refreshElement}
                                saveTemplate={props.saveTemplate}
                                isDragging={props.isDragging}
                            />
                        )
                        break
                }

                return lineOut
            }

            if (editableOptions.type == 'categorySubMenu') {
                // categoryGroup: "Fonts",
                // children: (5) [{…}, {…}, {…}, {…}, {…}],
                // menuItems: (5) ['P', 'H1', 'H2', 'H3', 'H4'],
                // newCategory: "Fonts",
                // type: "categorySubMenu"

                return (
                    <RenderSelectionComponent
                        key={`selection_${editableOptions.newCategory}_${editableOptions.categoryGroup}`}
                        editableOptions={editableOptions}
                        renderCatGroup={renderCatGroup}
                        globalColours={globalColours}
                        currentCatGroup={currentCatGroup}
                        initialSubCategory={
                            editableOptions.categoryGroup === 'Buttons' || editableOptions.categoryGroup === 'Fonts'
                                ? props.globalStyleFocus?.subCategory
                                : undefined
                        }
                    />
                )
            }

            var foundClusters = []
            var currentCat = ''
            var foundOptsInCat = 0

            var linesOut = editableOptions.map((theOpt, index) => {
                var lineOut = []
                var theCat = ''
                if (theOpt.category) {
                    theCat = theOpt.category
                }

                if (theCat != currentCat) {
                    if (showBreakers && foundOptsInCat > 0) {
                        lineOut.push(<Breaker key={'breaker_' + theCat + '_' + index} cat={theCat} />)
                    }
                    currentCat = theCat
                }

                if (!requiredToShowProcessor(theOpt, localVariables, thisElement)) return null

                const forcedClustersByCategory = [
                    { value: 'Padding', label: 'paddingForced' },
                    { value: 'Margin', label: 'marginForced' },
                    { value: 'BorderRadius', label: 'borderRadius' }
                ]

                var foundInCluster = foundClusters.find(
                    (c) =>
                        forcedClustersByCategory.find(
                            (c) => c.value == theOpt.category || (theOpt.newCategory && c.value == theOpt.newCategory)
                        ) && c.clusterLabel == theOpt.category + '_' + theOpt.categoryGroup
                )
                if (
                    !foundInCluster &&
                    forcedClustersByCategory.find(
                        (c) => c.value == theOpt.category || (theOpt.newCategory && c.value == theOpt.newCategory)
                    )
                ) {
                    foundInCluster = true
                    let clusterTypeFound = theOpt.clusterType
                    let clusterLabel = clusterTypeFound
                    let isForcedCluster = forcedClustersByCategory.find((c) => c.value == theOpt.category)
                    // Gathering by cluster needs both keys: the group name to filter on and the type to
                    // switch on. Options with only a clusterType stay forced together by category.
                    if (theOpt.cluster && theOpt.clusterType) isForcedCluster = false
                    if (isForcedCluster) {
                        clusterTypeFound = isForcedCluster.label
                        clusterLabel = theOpt.category + '_' + theOpt.categoryGroup
                    }
                    clusterTypeFound = clusterTypeFound.replace(' ', '').toLowerCase()
                    foundClusters.push({
                        clusterType: clusterTypeFound,
                        clusterLabel: clusterLabel
                    })

                    var clusterMembers = editableOptions.filter((e) => e.cluster == clusterTypeFound)
                    if (isForcedCluster) {
                        clusterMembers = editableOptions.filter(
                            (e) => e.category + '_' + e.categoryGroup == clusterLabel
                        )
                    }
                    if (clusterMembers) {
                        clusterMembers = clusterMembers.map((c, sIndex) => {
                            return getComponentType(c, sIndex, globalColours, currentCatGroup)
                        })
                    }
                    var cStyle = {}

                    switch (clusterTypeFound) {
                        case 'border':
                            var borderWidth = '0px'
                            var borderColor = '#000000'
                            var borderStyle = 'solid'

                            var cBorderWidth = editableOptions.find(
                                (e) => e.cluster == clusterTypeFound && e.clusterCssType == 'borderWidth'
                            )
                            if (cBorderWidth && cBorderWidth.value) borderWidth = cBorderWidth.value
                            var cBorderStyle = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'borderStyle'
                            )
                            if (cBorderStyle && cBorderStyle.value) borderStyle = cBorderStyle.value
                            var cBorderColor = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'borderColor'
                            )
                            if (cBorderColor && cBorderColor.value) borderColor = cBorderColor.value

                            cStyle.borderTop = borderWidth + ' ' + borderStyle + ' ' + borderColor
                            cStyle.width = '100%'
                            cStyle.position = 'absolute'
                            cStyle.top = '50%'
                            cStyle.transform = 'translateY(-50%)'

                            var html = <div style={cStyle} />
                            lineOut.push(
                                <ClusterHolder
                                    key={'cluster_' + clusterTypeFound + '_' + index}
                                    clusterMembers={clusterMembers}
                                    title={theOpt.clusterLabel}
                                    html={html}
                                    index={index}
                                />
                            )
                            break
                        case 'font':
                            var fontSize = '14px'
                            var fontFamily = 'arial'
                            var fontColor = '#000000'

                            var cfontColor = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'color'
                            )
                            if (cfontColor && cfontColor.value) fontColor = cfontColor.value
                            var cfontFamily = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'fontFamily'
                            )
                            if (cfontFamily && cfontFamily.value) fontFamily = cfontFamily.value
                            var cfontSize = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'fontSize'
                            )
                            if (cfontSize && cfontSize.value) fontSize = cfontSize.value

                            cStyle.width = '100%'
                            cStyle.position = 'absolute'
                            cStyle.top = '50%'
                            cStyle.transform = 'translateY(-50%)'
                            cStyle.textAlign = 'center'
                            cStyle.fontFamily = fontFamily
                            cStyle.fontSize = fontSize
                            cStyle.color = fontColor

                            var html = <div style={cStyle}>A</div>
                            lineOut.push(
                                <ClusterHolder
                                    key={'cluster_' + clusterTypeFound + '_' + index}
                                    clusterMembers={clusterMembers}
                                    title={theOpt.clusterLabel}
                                    html={html}
                                    index={index}
                                />
                            )
                            break
                        case 'socialicon':
                            var imgWidth = ''
                            var imgUrl = ''

                            var cimgWidth = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'iconSize'
                            )
                            if (cimgWidth && cimgWidth.value) imgWidth = cimgWidth.value
                            var cimgUrl = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'iconType'
                            )
                            if (cimgUrl && cimgUrl.value)
                                imgUrl =
                                    'https://cdn1.ourmailsender.com/siteContent/assets/templates/socialMediaIcons/x-' +
                                    cimgUrl.value +
                                    '.png'

                            cStyle.width = '100%'
                            cStyle.height = '100%'
                            cStyle.backgroundImage = 'url(' + imgUrl + ')'
                            cStyle.backgroundSize = imgWidth + ' ' + imgWidth
                            cStyle.backgroundPosition = 'center center'
                            cStyle.backgroundRepeat = 'no-repeat'

                            var html = <div style={cStyle}></div>
                            lineOut.push(
                                <ClusterHolder
                                    key={'cluster_' + clusterTypeFound + '_' + index}
                                    clusterMembers={clusterMembers}
                                    title={theOpt.clusterLabel}
                                    html={html}
                                    index={index}
                                />
                            )
                            break
                        case 'colourimage':
                            var bgColor = ''
                            var bgImg = ''

                            var cBgColor = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'backgroundColor'
                            )
                            if (cBgColor && cBgColor.value) bgColor = cBgColor.value
                            var cBgImg = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'backgroundImage'
                            )
                            if (cBgImg && cBgImg.value) bgImg = cBgImg.value

                            cStyle.width = '100%'
                            cStyle.height = '100%'
                            cStyle.backgroundColor = bgColor
                            if (bgImg) {
                                cStyle.backgroundImage = 'url(' + bgImg + ')'
                                cStyle.backgroundSize = '100% 100%'
                                cStyle.backgroundPosition = 'center center'
                                cStyle.backgroundRepeat = 'no-repeat'
                            }

                            var html = <div style={cStyle}></div>
                            lineOut.push(
                                <ClusterHolder
                                    key={'cluster_' + clusterTypeFound + '_' + index}
                                    clusterMembers={clusterMembers}
                                    title={theOpt.clusterLabel}
                                    html={html}
                                    index={index}
                                />
                            )
                            break
                        case 'padding':
                            var imgPaddingLeft = '0px'
                            var imgPaddingLeftVal = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'paddingLeft'
                            )
                            if (imgPaddingLeftVal)
                                imgPaddingLeft = Number(imgPaddingLeftVal.value.replace('px', '')) / 4 + 'px'
                            imgPaddingLeft += ' solid green'

                            var imgPaddingRight = '0px'
                            var imgPaddingRightVal = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'paddingRight'
                            )
                            if (imgPaddingRightVal)
                                imgPaddingRight = Number(imgPaddingRightVal.value.replace('px', '')) / 4 + 'px'
                            imgPaddingRight += ' solid green'

                            var imgPaddingTop = '0px'
                            var imgPaddingTopVal = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'paddingTop'
                            )
                            if (imgPaddingTopVal)
                                imgPaddingTop = Number(imgPaddingTopVal.value.replace('px', '')) / 4 + 'px'
                            imgPaddingTop += ' solid green'

                            var imgPaddingBottom = '0px'
                            var imgPaddingBottomVal = editableOptions.find(
                                (e) => e.cluster == theOpt.cluster && e.clusterCssType == 'paddingBottom'
                            )
                            if (imgPaddingBottomVal)
                                imgPaddingBottom = Number(imgPaddingBottomVal.value.replace('px', '')) / 4 + 'px'
                            imgPaddingBottom += ' solid green'

                            var html = (
                                <div
                                    style={{
                                        borderLeft: imgPaddingLeft,
                                        borderRight: imgPaddingRight,
                                        borderTop: imgPaddingTop,
                                        borderBottom: imgPaddingBottom,
                                        width: '100%',
                                        height: '100%'
                                    }}
                                ></div>
                            )
                            lineOut.push(
                                <ClusterHolder
                                    key={'cluster_' + clusterTypeFound + '_' + index}
                                    clusterMembers={clusterMembers}
                                    title={theOpt.clusterLabel}
                                    html={html}
                                    index={index}
                                />
                            )
                            break
                        case 'borderradius': {
                            const borderRadiusKey =
                                clusterMembers?.[0]?.props?.path != null
                                    ? `${clusterLabel}_${clusterMembers[0].props.path}`
                                    : `${clusterLabel}_${clusterTypeFound}_${index}`
                            lineOut.push(
                                <BorderRadiusHolder
                                    key={borderRadiusKey}
                                    updateVal={updateVal}
                                    clusterMembers={clusterMembers}
                                    index={index}
                                />
                            )
                            break
                        }
                        case 'paddingforced':
                        case 'marginforced': {
                            const marginPaddingKey =
                                clusterMembers?.[0]?.props?.path != null
                                    ? `${clusterLabel}_${clusterMembers[0].props.path}`
                                    : `${clusterLabel}_${clusterTypeFound}_${index}`
                            lineOut.push(
                                <MarginPaddingHolder
                                    key={marginPaddingKey}
                                    updateVal={updateVal}
                                    clusterMembers={clusterMembers}
                                    index={index}
                                    type={clusterTypeFound == 'paddingforced' ? 'padding' : 'margin'}
                                />
                            )
                            break
                        }
                    }
                }
                if (!foundInCluster) {
                    lineOut.push(getComponentType(theOpt, index, globalColours, currentCatGroup))
                }

                foundOptsInCat++
                return lineOut
            })

            return linesOut
        },
        [currentTab, thisElement, props, updateVal, refreshElement]
    )

    if (loaded === false || !thisElement) {
        return <LoadingIcons iconType='2' />
    }

    var localVariables = {}

    if (thisElement.localVariables) {
        localVariables = thisElement.localVariables
    }

    var editableOptions = []
    function findElements(thisEl, thePath) {
        if (thisEl.hasOwnProperty('contentDate') && thePath != '') {
            return
        }
        Object.keys(thisEl).map((theEl, index) => {
            var tmpPath = thePath
            var theLine = thisEl[theEl]
            if (typeof theLine === 'object' && theLine != null) {
                if (isNaN(theEl) == false) {
                    tmpPath += '[' + theEl + '].'
                } else {
                    tmpPath += theEl + '.'
                }
                if (theLine.editable && typeof theLine.editable == 'string') {
                    if (theLine.editable.indexOf('localVariables_') > 0) {
                        theLine.editable = DragDropFunctions.lookupLocalVariables(localVariables, theLine.editable)
                    }
                }
                if (theLine.editable || theLine.previewOnly) {
                    theLine.path = tmpPath.substr(0, tmpPath.length - 1)
                    editableOptions.push(theLine)
                    return
                }

                findElements(theLine, tmpPath)
            }
        })

        return
    }
    findElements(thisElement, '')

    var editableTabs = []
    if (thisElement && (thisElement.editableTabs || thisElement.editableTabsNew)) {
        editableTabs = thisElement.editableTabsNew || thisElement.editableTabs
    }

    // Landing-page Styles order comes from theme editableOrder / first-seen newCategory
    // (baseLandingPageTheme.json / Theme 100): Page, Layout, Elements, Buttons, Fonts, Links, Forms.
    // Do not splice categories here.
    editableOptions = sortBy(editableOptions, ['editableOrder', 'categoryGroup', 'category'])

    var currentCatGroup = -1
    var defaultCatGroup = undefined

    if (thisElement.defaultCatagoryGroup) {
        defaultCatGroup = thisElement.defaultCatagoryGroup
    }

    //get global colours
    var globalColours = DragDropFunctions.getTemplateColours(props.elementsData)

    var blocksContentStyle = {}
    if (props.noTitleBar == true) {
        blocksContentStyle.height = 'calc(100vh - 170px)'
    }

    return (
        <>
            {thisElement.name && (
                <PanelHeader
                    title={thisElement.name}
                    subtitle={!!thisElement.warning && thisElement.warning}
                    onBack={props.deselectElement}
                    hideBorder={true}
                />
            )}
            {externalDataError && (
                <div className='editorExternalDataError'>
                    <div className='mar-b15'>There has been a problem connecting to {thisElement.name}.</div>
                    {thisElement.applicationId && (
                        <>
                            <div className='mar-b20'>Please try reconnecting your {thisElement.name} integration.</div>
                            <Button
                                text={`Reconnect to ${thisElement.name}`}
                                onClick={() => {
                                    window.open('/cp/company/integrations?show=' + thisElement.applicationId, '_blank')
                                }}
                            />
                        </>
                    )}
                    {!thisElement.applicationId && (
                        <>
                            <div>Please try again later.</div>
                        </>
                    )}
                </div>
            )}
            {!externalDataError && (
                <>
                    {editableTabs.length > 1 && (
                        <TabsSimple
                            tabs={editableTabs.map((tab, index) => {
                                return { label: i18n.t('templates:add.drag.tabs.' + tab), value: index }
                            })}
                            selectedTab={editableTabs.indexOf(currentTab)}
                            onChange={(tab) => {
                                changeTab(editableTabs[tab])
                            }}
                            className='mar-b20 dndEditorv2-tabs'
                        />
                    )}

                    <div className='blocksContent' style={blocksContentStyle}>
                        {externalDataError && <div className='externalDataError'></div>}
                        {!externalDataError &&
                            (() => {
                                const categorizedOptions = buildCategoryHierarchy(editableOptions, currentTab)
                                currentCatGroup = -1
                                const expanderCategories = categorizedOptions.filter(
                                    (c) => c.isParent && c.name && c.name !== 'General'
                                )
                                const defaultOpenNames = expanderCategories
                                    .filter((c, i) => i === 0 || c.expanded)
                                    .flatMap((c) => {
                                        const names = [c.name]
                                        if (/^Column \d+$/.test(c.name)) {
                                            names.push(c.name + '::Background')
                                        }
                                        return names
                                    })
                                const isInspector = !!(props.selectedElement && props.selectedElement.isSelected)
                                const requestedOpen = isInspector
                                    ? Array.isArray(props.expandedElements)
                                        ? props.expandedElements
                                        : defaultOpenNames
                                    : props.expandedElements?.length > 0
                                      ? props.expandedElements
                                      : defaultOpenNames
                                const matchingOpen = requestedOpen.filter(
                                    (name) =>
                                        expanderCategories.some((c) => c.name === name) ||
                                        // Keep every nested group under a column, not just
                                        // Background, so each one remembers being opened.
                                        /^Column \d+::/.test(name)
                                )
                                const openNames = matchingOpen.length > 0 ? matchingOpen : defaultOpenNames
                                return categorizedOptions.map((category, index) => {
                                    if (category.isParent) {
                                        // Render children and keep only those that actually produce content
                                        const renderedChildren = category.children
                                            .map((childCat, childIndex) => {
                                                const childContent = renderCatGroup(
                                                    childCat.items,
                                                    globalColours,
                                                    childCat.name
                                                )
                                                const hasChildContent =
                                                    childContent &&
                                                    (Array.isArray(childContent)
                                                        ? childContent.some(Boolean)
                                                        : !!childContent)
                                                if (!hasChildContent) return null

                                                const useNestedExpander =
                                                    /^Column \d+$/.test(category.name) &&
                                                    childCat.name &&
                                                    childCat.name !== category.name &&
                                                    childCat.name !== 'General'

                                                if (!useNestedExpander) return childContent

                                                const nestedKey = category.name + '::' + childCat.name
                                                const parentExpanded = openNames.indexOf(category.name) !== -1
                                                const nestedExpanded =
                                                    openNames.indexOf(nestedKey) !== -1 ||
                                                    (openNames.indexOf(nestedKey + '::__closed') === -1 &&
                                                        parentExpanded)

                                                return (
                                                    <Expander
                                                        key={'category_' + nestedKey}
                                                        label={categoryLabel(childCat.name)}
                                                        dataCategory={nestedKey}
                                                        expanded={nestedExpanded}
                                                        noMarginTop={childIndex === 0}
                                                        onExpand={() => {
                                                            const newElements = openNames.filter(
                                                                (element) => element !== nestedKey + '::__closed'
                                                            )
                                                            if (newElements.indexOf(nestedKey) === -1) {
                                                                newElements.push(nestedKey)
                                                            }
                                                            props.setExpandedElements(newElements)
                                                        }}
                                                        onCollapse={() => {
                                                            const newElements = openNames.filter(
                                                                (element) => element !== nestedKey
                                                            )
                                                            if (newElements.indexOf(nestedKey + '::__closed') === -1) {
                                                                newElements.push(nestedKey + '::__closed')
                                                            }
                                                            props.setExpandedElements(newElements)
                                                        }}
                                                    >
                                                        {childContent}
                                                    </Expander>
                                                )
                                            })
                                            .filter(Boolean)

                                        if (category.selectionOptions && category.selectionOptions.length > 0) {
                                            category.selectionOptions.forEach(
                                                (selectionOption, selectionOptionIndex) => {
                                                    renderedChildren.push(
                                                        renderCatGroup(
                                                            selectionOption,
                                                            globalColours,
                                                            selectionOption.name
                                                        )
                                                    )
                                                }
                                            )
                                        }

                                        if (renderedChildren.length === 0) {
                                            return null
                                        }

                                        if (category.name === '' || category.name === 'General') {
                                            return (
                                                <div
                                                    key={'category_plain_' + (category.name || index)}
                                                    className='dndEditorv2-blocksContent-noChildren'
                                                >
                                                    {renderedChildren}
                                                </div>
                                            )
                                        }

                                        const expanded = openNames.indexOf(category.name) !== -1
                                        return (
                                            <Expander
                                                isFirst={
                                                    expanderCategories[0] &&
                                                    expanderCategories[0].name === category.name
                                                }
                                                key={`category_${category.name}`}
                                                label={categoryLabel(category.name)}
                                                dataCategory={category.name}
                                                expanded={expanded}
                                                onExpand={() => {
                                                    const newElements = [...openNames]
                                                    if (newElements.indexOf(category.name) === -1) {
                                                        newElements.push(category.name)
                                                    }
                                                    props.setExpandedElements(newElements)
                                                }}
                                                onCollapse={() => {
                                                    props.setExpandedElements(
                                                        openNames.filter((element) => element !== category.name)
                                                    )
                                                }}
                                            >
                                                {renderedChildren}
                                            </Expander>
                                        )
                                    } else {
                                        const rendered = renderCatGroup(category.items, globalColours, category.name)
                                        if (!rendered || (Array.isArray(rendered) && !rendered.some(Boolean)))
                                            return null
                                        return rendered
                                    }
                                })
                            })()}
                    </div>
                </>
            )}
        </>
    )
}

function RenderSelectionComponent({
    editableOptions,
    renderCatGroup,
    globalColours,
    currentCatGroup,
    initialSubCategory
}) {
    const tabFromFocus = (() => {
        if (!initialSubCategory) return 0
        const idx = editableOptions.menuItems.findIndex(
            (item) => String(item).toLowerCase() === String(initialSubCategory).toLowerCase()
        )
        return idx >= 0 ? idx : 0
    })()
    const [selectedTab, setSelectedTab] = useState(tabFromFocus)

    useEffect(() => {
        setSelectedTab(tabFromFocus)
    }, [tabFromFocus])

    const filteredOptions = editableOptions.children.filter(
        (editableOption) => editableOption.newCategorySubCategory == editableOptions.menuItems[selectedTab]
    )

    return (
        <>
            <TabsSimple
                tabs={editableOptions.menuItems.map((menuItem, menuItemIndex) => {
                    return { label: menuItem, value: menuItemIndex }
                })}
                selectedTab={selectedTab}
                onChange={(tab) => {
                    setSelectedTab(tab)
                }}
                className='mar-b20 dndEditorv2-tabs'
            />
            {renderCatGroup(filteredOptions, globalColours, currentCatGroup, false)}
        </>
    )
}
